import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { formatCurrencyCompact, getYAxisConfig } from './SpendChart/helper';
import { formatAmountWithCommas } from '../shared/UtilFunctions';
import { CustomXTickText } from './SpendChart/CustomXTickText';
import { CustomYTickText } from './SpendChart/CustomYTickText';
import { SELECTION_MODE } from '../shared/AppConstants';

const CURRENT_SPEND_COLOR = '#52A077'; // Spruce — no token equivalent

// No token equivalents for this secondary CI palette
const CI_COLORS = [
  '#7B2D8B',
  '#C75000',
  '#007A6E',
  '#A50034',
  '#3D5A99',
  '#5C8A00',
  '#9C4B00',
  '#4A4A4A'
];

function getSupplierLabel(supplier) {
  return supplier.supplierName &&
    supplier.supplierName !== supplier.supplierNumber
    ? `${supplier.supplierNumber} - ${supplier.supplierName}`
    : supplier.supplierNumber;
}

function GraphSeriesIcon({ color, variant = 'circle', patternId }) {
  const theme = useTheme();
  if (variant === 'ci') {
    return (
      <svg
        width={12}
        height={12}
        style={{ flexShrink: 0, display: 'inline-block' }}
      >
        <defs>
          <pattern
            id={patternId}
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
            patternTransform="rotate(45)"
          >
            <rect width="6" height="6" fill={color} fillOpacity={0.25} />
            <line x1="0" y1="0" x2="0" y2="6" stroke={color} strokeWidth="3" />
          </pattern>
        </defs>
        <rect
          width={12}
          height={12}
          rx={2}
          fill={theme.fuel6Tokens.JddcColorBackgroundPrimary}
        />
        <rect width={12} height={12} rx={2} fill={`url(#${patternId})`} />
      </svg>
    );
  }

  if (variant === 'line') {
    return (
      <span
        style={{
          display: 'inline-block',
          width: 18,
          height: 3,
          backgroundColor: color,
          flexShrink: 0
        }}
      />
    );
  }

  return (
    <span
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: color,
        flexShrink: 0
      }}
    />
  );
}

function CustomTooltip({
  active,
  payload,
  label,
  programColorMap,
  spendShiftDisplayMode,
  capacityLines,
  supplierView = false
}) {
  const theme = useTheme();
  if (!active || !payload || payload.length === 0) return null;

  // Filter out capacity reference lines from payload (they appear as payload entries too)
  const barPayload = payload.filter(
    (e) =>
      e.dataKey !== 'totalCurrentCapacity' &&
      e.dataKey !== 'totalMaxCapacity' &&
      (!supplierView || Number(e.value) !== 0)
  );

  const formatValue = (val) =>
    spendShiftDisplayMode === 'pieces'
      ? val.toLocaleString('en-US', { maximumFractionDigits: 0 })
      : supplierView
        ? formatCurrencyCompact(val)
        : formatAmountWithCommas(val, true);

  return (
    <div
      style={{
        backgroundColor: theme.fuel6Tokens.JddcColorBackgroundStrong,
        borderRadius: 4,
        color: theme.fuel6Tokens.JddcColorTextInverse,
        fontSize: '0.875rem',
        fontWeight: 'normal',
        padding: '0.75rem',
        zIndex: 1,
        minWidth: 220
      }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{label}</div>
      {barPayload.map((entry, index) => {
        const color = programColorMap[entry.dataKey] || CURRENT_SPEND_COLOR;
        const isCiProject = String(entry.dataKey).startsWith('ci_');
        return (
          <div
            key={`tooltip-${index}`}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.25rem',
              gap: '1rem'
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <GraphSeriesIcon
                color={color}
                variant={isCiProject ? 'ci' : 'circle'}
                patternId={`tooltipCiPattern-${entry.dataKey}`}
              />
              {entry.name}
            </span>
            <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {formatValue(entry.value)}
            </span>
          </div>
        );
      })}
      {!supplierView && (
        <div
          style={{
            borderTop: `1px solid ${theme.fuel6Tokens.JddcColorBorderSubtle}`,
            marginTop: '0.25rem',
            paddingTop: '0.25rem',
            display: 'flex',
            justifyContent: 'space-between',
            fontWeight: 'bold'
          }}
        >
          <span>
            {spendShiftDisplayMode === 'pieces'
              ? 'Total Pieces'
              : 'Total Spend'}
          </span>
          <span>
            {formatValue(
              barPayload.reduce((sum, entry) => sum + (entry.value || 0), 0)
            )}
          </span>
        </div>
      )}
      {/* Capacity lines in tooltip */}
      {capacityLines && (
        <>
          <div
            style={{
              borderTop: `1px solid ${theme.fuel6Tokens.JddcColorBorderSubtle}`,
              marginTop: '0.25rem',
              paddingTop: '0.25rem',
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <GraphSeriesIcon
                color={theme.fuel6Tokens.JddcColorBackgroundWarning}
                variant="line"
              />
              Total Current Capacity
            </span>
            <span style={{ fontWeight: 'bold' }}>
              {capacityLines.current.toLocaleString('en-US', {
                maximumFractionDigits: 0
              })}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '0.25rem'
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <GraphSeriesIcon
                color={theme.fuel6Tokens.JddcColorDataNeutral}
                variant="line"
              />
              Total Max Capacity
            </span>
            <span style={{ fontWeight: 'bold' }}>
              {capacityLines.max.toLocaleString('en-US', {
                maximumFractionDigits: 0
              })}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// Renders the total-spend label above the tallest point of the stacked bar.
// Attached to an invisible, zero-value "marker" bar stacked on top of all
// other series so its y-position always reflects the true top of the
// positive stack (recharts computes it from the cumulative stack offset),
// regardless of whether the net total differs from any individual segment.
function StackTotalLabel({ x, y, width, value, spendShiftDisplayMode }) {
  const theme = useTheme();
  if (value === undefined || value === null) return null;
  const label =
    spendShiftDisplayMode === 'pieces'
      ? value.toLocaleString('en-US', { maximumFractionDigits: 0 })
      : formatCurrencyCompact(value);
  return (
    <text
      x={x + width / 2}
      y={y - 8}
      fill={theme.fuel6Tokens.JddcColorTextPrimary}
      textAnchor="middle"
      fontSize={14}
      fontWeight="bold"
    >
      {label}
    </text>
  );
}

// Custom Y-axis tick that formats as integer (no $) for pieces mode
function CustomYTickPieces(props) {
  const theme = useTheme();
  const { x, y, payload } = props;
  const value = payload?.value;
  if (value === undefined || value === null) return null;
  const label =
    Math.abs(value) >= 1000 ? `${(value / 1000).toFixed(0)}k` : String(value);
  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor="end"
      fill={theme.fuel6Tokens.JddcColorTextSecondary}
      fontSize={12}
    >
      {label}
    </text>
  );
}

export default function SupplierSpendShiftGraph({
  spendData,
  spendShiftDisplayMode,
  selectionMode,
  errorStatus,
  supplierBreakdown = [],
  supplierCount = supplierBreakdown.length
}) {
  const theme = useTheme();
  const [viewBy, setViewBy] = useState('total');
  const canShowSupplierView =
    supplierCount > 1 && supplierBreakdown.length === supplierCount;
  const isSupplierView = viewBy === 'supplier' && canShowSupplierView;

  useEffect(() => {
    if (supplierCount <= 1) setViewBy('total');
  }, [supplierCount]);

  const hasCapacity =
    typeof spendData?.totalCapacity?.totalCurrentCapacity === 'number' &&
    typeof spendData?.totalCapacity?.totalMaxCapacity === 'number';
  const showCapacityLines =
    spendShiftDisplayMode === 'pieces' &&
    selectionMode === SELECTION_MODE.SINGLE_SUPPLIER &&
    hasCapacity &&
    !isSupplierView;

  const { chartData, dataKeys, programColorMap, legendItems } = useMemo(() => {
    if (!spendData)
      return {
        chartData: [],
        dataKeys: [],
        programColorMap: {},
        legendItems: []
      };

    const { fiscalYears, currentSpends, pdpPrograms, totalSpends } = spendData;
    const supplierColors = [
      theme.fuel6Tokens.JddcColorDataCategoricalLime,
      theme.fuel6Tokens.JddcColorDataCategoricalCobalt,
      theme.fuel6Tokens.JddcColorDataCategoricalEmber,
      theme.fuel6Tokens.JddcColorDataCategoricalViolet,
      theme.fuel6Tokens.JddcColorDataCategoricalCyan,
      theme.fuel6Tokens.JddcColorDataCategoricalRose,
      theme.fuel6Tokens.JddcColorDataCategoricalFuchsia,
      theme.fuel6Tokens.JddcColorDataCategoricalSlate
    ];

    // Build a stable color map for PDP programs
    const pdpColors = [
      theme.fuel6Tokens.JddcColorDataCategoricalCobalt,
      theme.fuel6Tokens.JddcColorDataCategoricalEmber,
      theme.fuel6Tokens.JddcColorDataCategoricalCyan,
      theme.fuel6Tokens.JddcColorDataCategoricalRose,
      theme.fuel6Tokens.JddcColorDataCategoricalViolet,
      theme.fuel6Tokens.JddcColorDataCategoricalLime,
      theme.fuel6Tokens.JddcColorDataCategoricalFuchsia,
      theme.fuel6Tokens.JddcColorDataCategoricalSlate
    ];
    const colorMap = {};
    pdpPrograms.forEach((prog, idx) => {
      const key = `pdp_${prog.csProgramId}`;
      colorMap[key] = pdpColors[idx % pdpColors.length];
    });

    // Build CI color map
    const ciColorMap = {};
    (spendData.ciProjects ?? []).forEach((proj, idx) => {
      const key = `ci_${proj.projectId}`;
      ciColorMap[key] = CI_COLORS[idx % CI_COLORS.length];
    });

    // Merge into main colorMap
    Object.assign(colorMap, ciColorMap);

    // Build time period categories
    const categories = [
      {
        label: 'R12 Invoiced',
        currentKey: 'r12InvoiceSpend',
        totalKey: 'r12InvoiceSpend',
        isFY: false
      },
      {
        label: 'R12 Forecasted',
        currentKey: 'r12ForecastSpend',
        totalKey: 'r12ForecastSpend',
        isFY: false
      },
      ...fiscalYears.map((fy, idx) => ({
        label: `FY${fy}`,
        fyIndex: idx,
        totalKey: 'fiscalYears',
        isFY: true
      }))
    ];

    const data = categories.map((cat) => {
      const entry = { name: cat.label };

      // Current Spend
      if (cat.currentKey) {
        entry.currentSpend = currentSpends[cat.currentKey].amount;
      } else if (cat.fyIndex != null) {
        entry.currentSpend = currentSpends.fiscalYears[cat.fyIndex].amount;
      }

      // PDP Programs
      pdpPrograms.forEach((prog) => {
        const key = `pdp_${prog.csProgramId}`;
        if (cat.currentKey) {
          entry[key] = prog[cat.currentKey].amount;
        } else if (cat.fyIndex != null) {
          entry[key] = prog.fiscalYears[cat.fyIndex].amount;
        }
      });

      // CI Projects ← ADD FROM HERE
      (spendData.ciProjects ?? []).forEach((proj) => {
        const key = `ci_${proj.projectId}`;
        if (cat.fyIndex != null) {
          entry[key] = proj.fiscalYears[cat.fyIndex]?.amount ?? 0;
        } else {
          entry[key] = 0; // R12 columns are always $0 for CI projects
        }
      });

      // Total Spend (for top-of-bar label)
      if (cat.totalKey === 'fiscalYears') {
        entry.totalSpend = totalSpends.fiscalYears[cat.fyIndex].amount;
      } else {
        entry.totalSpend = totalSpends[cat.totalKey].amount;
      }

      // Zero-value marker stacked on top of every other series so its
      // rendered position always matches the true top of the bar.
      entry.stackTopMarker = 0;

      supplierBreakdown.forEach((supplier, idx) => {
        const key = `supplier_${idx}`;
        entry[key] = cat.currentKey
          ? (supplier.spendData.totalSpends[cat.currentKey]?.amount ?? 0)
          : (supplier.spendData.totalSpends.fiscalYears[cat.fyIndex]?.amount ??
            0);
      });

      // Capacity lines: only on FY columns, null on R12 columns so the Line doesn't extend left
      const capCurrent = spendData.totalCapacity?.totalCurrentCapacity;
      const capMax = spendData.totalCapacity?.totalMaxCapacity;
      entry.totalCurrentCapacity = capCurrent;
      entry.totalMaxCapacity = capMax;

      return entry;
    });

    const keys = [
      'currentSpend',
      ...pdpPrograms.map((prog) => `pdp_${prog.csProgramId}`)
    ];

    const supplierKeys = supplierBreakdown.map((_, idx) => `supplier_${idx}`);
    supplierKeys.forEach((key, idx) => {
      colorMap[key] =
        idx < supplierColors.length
          ? supplierColors[idx]
          : `hsl(${((idx * 137.508 + 32) % 360).toFixed(3)} 70% 45%)`;
    });

    const totalLegend = [
      {
        key: 'currentSpend',
        label:
          spendShiftDisplayMode === 'pieces'
            ? 'Current Pieces'
            : 'Current Spend',
        color: CURRENT_SPEND_COLOR
      },
      ...pdpPrograms.map((prog) => ({
        key: `pdp_${prog.csProgramId}`,
        label: `#${prog.csProgramId} ${prog.csProgramName || prog.unitCode}`,
        color: colorMap[`pdp_${prog.csProgramId}`]
      }))
    ];

    const supplierLegend = supplierBreakdown.map((supplier, idx) => ({
      key: supplierKeys[idx],
      label: getSupplierLabel(supplier),
      color: colorMap[supplierKeys[idx]]
    }));

    return {
      chartData: data,
      dataKeys: isSupplierView ? supplierKeys : keys,
      programColorMap: colorMap,
      legendItems: isSupplierView ? supplierLegend : totalLegend
    };
  }, [
    spendData,
    spendShiftDisplayMode,
    supplierBreakdown,
    isSupplierView,
    theme
  ]);

  if (!spendData || chartData.length === 0) return null;

  // Compute Y-axis range from data
  const maxTotal = Math.max(...chartData.map((d) => d.totalSpend || 0));
  const minValue = isSupplierView
    ? 0
    : Math.min(
        ...chartData.flatMap((d) =>
          dataKeys
            .filter((k) => k !== 'currentSpend')
            .map((k) => d[k] || 0)
            .filter((v) => v < 0)
        ),
        0
      );

  // When showing capacity lines, ensure Y-axis extends to cover them
  const capacityCurrent = spendData.totalCapacity?.totalCurrentCapacity;
  const capacityMax = spendData.totalCapacity?.totalMaxCapacity;
  const effectiveMax = showCapacityLines
    ? Math.max(maxTotal, capacityMax) * 1.1
    : maxTotal * 1.1;

  // Generate uniform, evenly-spaced Y-axis ticks
  const { ticks: yTicks, domain: yDomain } = getYAxisConfig(
    minValue,
    effectiveMax
  );

  // Capacity info passed to tooltip so it can display the values
  const capacityLines =
    showCapacityLines && !isSupplierView
      ? { current: capacityCurrent, max: capacityMax }
      : null;

  return (
    <Box sx={{ mt: 2 }}>
      {supplierCount > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Typography variant="labelMedium">View by:</Typography>
          <ToggleButtonGroup
            exclusive
            value={viewBy}
            onChange={(_event, newValue) => {
              if (newValue) setViewBy(newValue);
            }}
            aria-label="View spend shift by"
            variant="segmented"
          >
            <ToggleButton value="total">Total</ToggleButton>
            <ToggleButton value="supplier" disabled={!canShowSupplierView}>
              Supplier
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={chartData}
          margin={{ top: 30, right: 30, left: 20, bottom: 5 }}
          stackOffset="sign"
        >
          <defs>
            {(spendData.ciProjects ?? []).map((_, i) => (
              <pattern
                key={`ciPattern-${i}`}
                id={`ciPattern-${i}`}
                patternUnits="userSpaceOnUse"
                width="6"
                height="6"
                patternTransform="rotate(45)"
              >
                <rect
                  width="6"
                  height="6"
                  fill={CI_COLORS[i % CI_COLORS.length]}
                  fillOpacity={0.25}
                />
                <line
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="6"
                  stroke={CI_COLORS[i % CI_COLORS.length]}
                  strokeWidth="3"
                />
              </pattern>
            ))}
          </defs>
          <CartesianGrid
            strokeDasharray="8 4"
            vertical={false}
            stroke={theme.fuel6Tokens.JddcColorBorderSubtle}
          />
          <XAxis
            dataKey="name"
            tick={<CustomXTickText />}
            interval={0}
            tickMargin={10}
            height={52}
          />
          <YAxis
            tick={
              spendShiftDisplayMode === 'pieces' ? (
                <CustomYTickPieces />
              ) : (
                <CustomYTickText />
              )
            }
            ticks={yTicks}
            domain={yDomain}
          />
          <Tooltip
            content={
              <CustomTooltip
                programColorMap={programColorMap}
                spendShiftDisplayMode={spendShiftDisplayMode}
                capacityLines={capacityLines}
                supplierView={isSupplierView}
              />
            }
            cursor={false}
            isAnimationActive={false}
          />
          <ReferenceLine
            y={0}
            stroke={theme.fuel6Tokens.JddcColorBorderSubtle}
            strokeDasharray="8 4"
          />

          {dataKeys.map((key, index) => {
            if (isSupplierView) {
              const supplier = supplierBreakdown[index];
              return (
                <Bar
                  key={key}
                  dataKey={key}
                  name={getSupplierLabel(supplier)}
                  stackId="stack"
                  fill={programColorMap[key]}
                  isAnimationActive={false}
                  maxBarSize={60}
                />
              );
            }
            const isCurrentSpend = key === 'currentSpend';
            const color = isCurrentSpend
              ? CURRENT_SPEND_COLOR
              : programColorMap[key];
            const programInfo = !isCurrentSpend
              ? spendData.pdpPrograms.find(
                  (p) => `pdp_${p.csProgramId}` === key
                )
              : null;
            const barName = isCurrentSpend
              ? spendShiftDisplayMode === 'pieces'
                ? 'Current Pieces'
                : 'Current Spend'
              : `#${programInfo?.csProgramId} ${programInfo?.csProgramName || programInfo?.unitCode || ''}`;

            return (
              <Bar
                key={key}
                dataKey={key}
                name={isCurrentSpend ? barName : `PDP: ${barName}`}
                stackId="stack"
                fill={color}
                isAnimationActive={false}
                maxBarSize={60}
              />
            );
          })}

          {!isSupplierView &&
            (spendData.ciProjects ?? []).map((proj, idx) => (
              <Bar
                key={`ci_${proj.projectId}`}
                dataKey={`ci_${proj.projectId}`}
                name={`CI: ${proj.projectId} ${proj.projectTitle}`}
                stackId="stack"
                fill={`url(#ciPattern-${idx % CI_COLORS.length})`}
                isAnimationActive={false}
                maxBarSize={60}
              />
            ))}

          {/* Invisible marker bar stacked last so the total-spend label is
              always anchored to the true top of the stack. */}
          <Bar
            dataKey="stackTopMarker"
            stackId="stack"
            fill="transparent"
            isAnimationActive={false}
            legendType="none"
          >
            <LabelList
              dataKey="totalSpend"
              content={
                <StackTotalLabel
                  spendShiftDisplayMode={spendShiftDisplayMode}
                />
              }
              position="top"
            />
          </Bar>

          {/* Capacity Lines — only render over FY columns (null values on R12 columns break the line there) */}
          {showCapacityLines && (
            <Line
              dataKey="totalCurrentCapacity"
              name="Total Current Capacity"
              stroke={theme.fuel6Tokens.JddcColorBackgroundWarning}
              strokeWidth={2}
              dot={{
                r: 4,
                fill: theme.fuel6Tokens.JddcColorBackgroundWarning,
                strokeWidth: 0
              }}
              connectNulls={false}
              isAnimationActive={false}
              legendType="none"
            />
          )}
          {showCapacityLines && (
            <Line
              dataKey="totalMaxCapacity"
              name="Total Max Capacity"
              stroke={theme.fuel6Tokens.JddcColorDataNeutral}
              strokeWidth={2}
              dot={{
                r: 4,
                fill: theme.fuel6Tokens.JddcColorDataNeutral,
                strokeWidth: 0
              }}
              connectNulls={false}
              isAnimationActive={false}
              legendType="none"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legend */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 2,
          mt: 1,
          px: 1
        }}
      >
        {legendItems.map((item) => (
          <Box
            key={item.key}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <GraphSeriesIcon color={item.color} />
            <Typography variant="bodySmall" sx={{ fontSize: '0.875rem' }}>
              {isSupplierView
                ? item.label
                : item.key === 'currentSpend'
                  ? item.label
                  : `PDP: ${item.label}`}
            </Typography>
          </Box>
        ))}

        {!isSupplierView &&
          (spendData.ciProjects ?? []).map((proj, idx) => (
            <Box
              key={`ci_${proj.projectId}`}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <GraphSeriesIcon
                color={CI_COLORS[idx % CI_COLORS.length]}
                variant="ci"
                patternId={`legendCiPattern-${proj.projectId}`}
              />
              <Typography variant="bodySmall" sx={{ fontSize: '0.875rem' }}>
                CI: #{proj.projectId} {proj.projectTitle}
              </Typography>
            </Box>
          ))}

        {/* Capacity legend entries — only shown in Pieces + Single Supplier mode */}
        {showCapacityLines && !isSupplierView && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GraphSeriesIcon
                color={theme.fuel6Tokens.JddcColorBackgroundWarning}
                variant="line"
              />
              <Typography variant="bodySmall" sx={{ fontSize: '0.875rem' }}>
                Total Current Capacity
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GraphSeriesIcon
                color={theme.fuel6Tokens.JddcColorDataNeutral}
                variant="line"
              />
              <Typography variant="bodySmall" sx={{ fontSize: '0.875rem' }}>
                Total Max Capacity
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Partial-error warning strip */}
      {errorStatus &&
        (errorStatus.invoiceSpendError ||
          errorStatus.forecastSpendError ||
          errorStatus.pdpProgramsError ||
          errorStatus.ciProjectsError) &&
        (() => {
          const failed = [
            errorStatus.invoiceSpendError && 'Invoice Spend',
            errorStatus.forecastSpendError && 'Forecast Spend',
            errorStatus.pdpProgramsError && 'PDP Programs',
            errorStatus.ciProjectsError && 'CI Projects'
          ].filter(Boolean);
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 1,
                px: 1,
                py: 0.75,
                backgroundColor:
                  theme.fuel6Tokens.JddcColorBackgroundWarningSubtle,
                border: `1px solid ${theme.fuel6Tokens.JddcColorBorderWarning}`,
                borderRadius: '4px'
              }}
            >
              <Box
                component="span"
                sx={{
                  color: theme.fuel6Tokens.JddcColorIconWarning,
                  fontSize: '1.1rem',
                  flexShrink: 0,
                  display: 'inline-flex'
                }}
              >
                <fuel-icon name="error_outlined" />
              </Box>
              <Typography
                variant="bodySmall"
                sx={{
                  fontSize: '0.8rem',
                  color: theme.fuel6Tokens.JddcColorTextPrimary
                }}
              >
                Partial data — {failed.join(', ')} could not be fully loaded.
                Chart may be incomplete.
              </Typography>
            </Box>
          );
        })()}
    </Box>
  );
}
